import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CommonDialog, CommonDialogHeader } from '../../Dialog';
import { DriverRatingsService } from '../../../api/services/driverRatingsService';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import {
  IDriverRating,
  IDriverRatingSummary,
} from '../../../types/api/response/driverratingsresponse';
import { driverSourceLabel } from './columns';

const PAGE_SIZE = 10;

interface Props {
  driver: IDriverRatingSummary | null;
  // The page's date range, so the drill-down shows the same ratings the totals were built from.
  from?: string;
  to?: string;
  isOpen: boolean;
  onClose: () => void;
}

const stars = (n: number | null) => (n == null ? '—' : '★'.repeat(n) + '☆'.repeat(5 - n));

// One driver's ratings, newest first, with the customer's comment — admin panel only (TRD §6.1).
export const DriverRatingsDetailDialog = ({ driver, from, to, isOpen, onClose }: Props) => {
  const dispatch = useDispatch();
  const [ratings, setRatings] = useState<IDriverRating[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [driver]);

  useEffect(() => {
    if (!isOpen || !driver?.driver_id) return;
    let cancelled = false;
    setLoading(true);
    DriverRatingsService.getDriverRatings(driver.driver_source, driver.driver_id, {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      page,
      size: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setRatings(res.content);
        setTotalPages(res.page.totalPages);
      })
      .catch(() => {
        if (!cancelled) {
          dispatch(
            showNotification({ message: 'Failed to fetch driver ratings.', severity: 'error' }),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, driver, from, to, isOpen, page]);

  if (!driver) return null;

  const title = driver.driver_name
    ? `${driver.driver_name} (${driver.driver_id})`
    : driver.driver_id;

  return (
    <CommonDialog
      isOpen={isOpen}
      header={<CommonDialogHeader title={`Ratings — ${title}`} onClose={onClose} />}
      body={
        <Box mt={1} sx={{ maxHeight: '65vh', overflowY: 'auto', pr: 0.5 }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {driverSourceLabel(driver.driver_source)} · average {driver.average_rating.toFixed(2)}{' '}
            over {driver.rating_count} rating{driver.rating_count === 1 ? '' : 's'}
          </Typography>
          <Divider />
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : ratings.length === 0 ? (
            <Typography variant="body2" color="text.disabled" py={2}>
              No ratings in this date range.
            </Typography>
          ) : (
            ratings.map((r) => (
              <Box key={r.rating_id} py={1.5} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body1" sx={{ color: 'warning.main', letterSpacing: 1 }}>
                    {stars(r.driver_rating)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(r.rated_at).toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {r.order_no ?? '—'}
                  {r.dispatch_id && r.dispatch_id !== r.order_no ? ` · ${r.dispatch_id}` : ''} ·
                  order rated {r.order_rating ?? '—'}/5
                </Typography>
                <Typography
                  variant="body2"
                  mt={0.5}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    color: r.comment ? 'inherit' : 'text.disabled',
                  }}
                >
                  {r.comment || 'No comment'}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      }
      footer={
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              size="small"
              disabled={loading || page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Newer
            </Button>
            <Typography variant="caption" color="text.secondary">
              Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={loading || page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Older
            </Button>
          </Box>
          <Button variant="outlined" size="small" onClick={onClose}>
            Close
          </Button>
        </Box>
      }
    />
  );
};
